import * as React from 'react';
import { History } from 'history';
import { getObjVal } from '../constants';

export type Props = {
  history: History;
  setFilters: (arg0: URLSearchParams) => void;
  filters: URLSearchParams | null;
  validators: { [name: string]: (arg: string) => any };
};

export type SetFilterValuesFunction = (
  arg0: Array<{ key: string; value: string | null }>,
) => void;

export type InjectedProps = {
  setFilterValues: SetFilterValuesFunction;
  filters: URLSearchParams;
};

/**
 * The root component for managing filter query params in route URLs
 */
const FilterManger = <WCProps extends InjectedProps = InjectedProps>(
  WrappedComponent: React.ComponentType<WCProps>,
) => {
  // The actual props that you need to supply to the wrapper
  type RequiredProps = Omit<WCProps, keyof InjectedProps> & Props;

  return class withFilterManager extends React.Component<RequiredProps> {
    // If the class is constructed with null filters, we create and dispatch a set
    // based on the current URL params. Because the dispatch is asynchronous, we
    // keep the dispatched initial params here so we can use them immediately
    // when the component mounts
    initialFilters: URLSearchParams | null = null;
    unregisterBackButtonListener: null | (() => void) = null;

    constructor(props: RequiredProps) {
      super(props);

      // When the route is initially constructed, update the filters in
      // the store to match what is supplied in the URL if there aren't
      // already filters in the store, in order to initialize the component
      // based on URL from a deep-linked bookmark
      if (props.filters === null) {
        this.initialFilters = this.setFiltersFromURL(props);
      } else {
        this.setURLFromFilters(props.filters);
      }
    }

    componentDidMount() {
      // Upon mounting, make sure the URL matches the desired filters.
      if (this.initialFilters) {
        this.setURLFromFilters(this.initialFilters);
      } else {
        this.setURLFromFilters(this.props.filters);
      }
      this.initialFilters = null;

      // Add a browser navigation history listener so that we can update filters
      // based on URL changes by browser back/forward navigation
      this.unregisterBackButtonListener = this.props.history.listen(
        (location, action) => {
          if (action === 'POP') {
            this.setFiltersFromURL(this.props);
          }
        },
      );
    }

    componentWillUnmount() {
      this.unregisterBackButtonListener && this.unregisterBackButtonListener();
    }

    /**
     * Compares the supplied history object's location to the supplied filters, and pushes a new
     * location if the current one does not match the filters
     * @param filters - the filters to put onto the URL
     * @param pushHistory {boolean} - whether to use history.push or history.replace, default to true
     * @param state {Object} - state to call push/replace with
     */
    setURLFromFilters = (
      filters: URLSearchParams | null,
      pushHistory: boolean = true,
      state: object | null = null,
    ) => {
      const { history } = this.props;
      const search = history.location.search.replace('?', ''),
        params = filters || new URLSearchParams();

      if (search !== (filters || '').toString()) {
        const path = `${history.location.pathname}?${params.toString()}`;

        const action = pushHistory ? history.push : history.replace;
        if (state) {
          action(path, state);
        } else {
          action(path);
        }
      }
    };

    /**
     * Sets the filters based on the current browser URL.
     * @param props - the current/next props object to use
     * @return {URLSearchParams} - returns the filters that have just been set
     */
    setFiltersFromURL = (props: Props): URLSearchParams => {
      const { history, setFilters, validators } = props;
      const currentUrlSearchParams = new URLSearchParams(
        history.location.search,
      );
      const validParams = new URLSearchParams();
      for (let [key, validator] of Object.entries(validators)) {
        const value = validator(currentUrlSearchParams.get(key) || '');
        if (value) {
          validParams.set(key, value);
        }
      }
      setFilters(validParams);
      return validParams;
    };

    setFilterValues = (
      values: Array<{ key: string; value: string | null }>,
    ) => {
      const { setFilters, validators, history } = this.props;
      const filters = this.props.filters || new URLSearchParams();
      const newFilters = new URLSearchParams(filters.toString());
      let pushHistory = false,
        state = undefined;

      values.forEach((pair) => {
        newFilters.delete(pair.key);
        if (pair.value) {
          newFilters.set(pair.key, pair.value);
        }

        // push new history entry if there is at least one filter validator that requires a push
        if (
          !pushHistory &&
          getObjVal(validators, `${values[0].key}.historyAction`, null) ===
            'push'
        ) {
          pushHistory = true;
        }
      });

      if (!pushHistory) {
        const updatedFilters = values
          .map((value) => value.key)
          .sort()
          .join('&');
        // push one copy of the current history entry if this is the first time to change this filter
        if (
          updatedFilters !==
          getObjVal(history, 'location.state.lastUpdatedFilters', null)
        ) {
          pushHistory = true;
        }

        // save this filter's name in history.location.state so we can compare it in subsequent updates
        state = {
          lastUpdatedFilters: updatedFilters,
        };
      }

      if (filters.toString() !== newFilters.toString()) {
        setFilters(newFilters);

        this.setURLFromFilters(newFilters, pushHistory, state);
      }
    };

    render() {
      const { filters, setFilters, ...rest } = this.props;

      return (
        //@ts-ignore: TODO - properly type generics
        <WrappedComponent
          setFilterValues={this.setFilterValues}
          filters={filters || this.initialFilters || new URLSearchParams()} // never send null filters to child components
          {...rest}
        />
      );
    }
  };
};

export default FilterManger;
